import { useEffect, useRef, useState, type FC, type KeyboardEvent } from "react";
import { Box, Button, ButtonBase, IconButton, Stack, TextField, Typography } from "@mui/material";
import { lighten, type SxProps, type Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type { ClassificationCustomerCustomizations, ClassificationTemplate } from "../api/classificationTemplate";
import { DynamicForm } from "../dynamicForm";
import { CLASSIFICATION_COLORS } from "./classificationColors";
import type { ClassificationPropertyKey, Criticality, Level, Score } from "./schemas";
import { HoverPopover, useHoverPopover } from "../shared/HoverPopover";
import { InfoIcon, XIcon } from "../shared/icons";

type Template = ClassificationTemplate;
type TemplateProperty = Template["properties"][number];

type HoverPreview = { propertyKey: ClassificationPropertyKey; level: Level };

type Props = {
  template: Template;
  customerCustomizations: ClassificationCustomerCustomizations;
  scores: Array<Score>;
  criticality?: Criticality | null;
  rationale?: string;
  isReadOnly?: boolean;
  onSave: (payload: { scores: Array<Score>; rationale: string }) => Promise<unknown>;
};

const useHoverPreview = (enabled: boolean) => {
  const [preview, setPreview] = useState<HoverPreview | null>(null);
  const suppressedRef = useRef(false);

  /*
   * Without this, clicking a cell to enter edit mode also paints the hover
   * preview on it, which reads as "I just selected that level" — but the
   * preview vanishes on the next mouse move. Suppress until first real move.
   */
  useEffect(() => {
    if (!enabled) return;
    const onMove = () => {
      suppressedRef.current = false;
    };
    window.addEventListener("pointermove", onMove, { once: true, passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [enabled]);

  const setPreviewUnlessSuppressed = (next: HoverPreview | null) => {
    if (next !== null && suppressedRef.current) return;
    setPreview(next);
  };

  return {
    preview,
    setPreview: setPreviewUnlessSuppressed,
    suppressUntilNextPointerMove: () => {
      suppressedRef.current = true;
    },
  };
};

export const ClassificationCard: FC<Props> = ({
  template,
  customerCustomizations,
  criticality,
  scores,
  rationale,
  isReadOnly = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Map<ClassificationPropertyKey, Level> | null>(null);
  const [draftRationale, setDraftRationale] = useState("");
  const updateMutation = useMutation({ mutationFn: onSave });

  const isEditing = draft !== null;
  const isInteractive = !isEditing && !isReadOnly;
  const hover = useHoverPreview(isEditing);
  const returnFocusRef = useRef(false);
  const savedScores = new Map(scores.map(s => [s.propertyKey, s.value]));
  const isDirty =
    draft !== null &&
    (draftRationale !== (rationale ?? "") ||
      draft.size !== savedScores.size ||
      Array.from(draft).some(([propertyKey, value]) => savedScores.get(propertyKey) !== value));
  DynamicForm.useReportUnsavedChange(isDirty);

  const levels = Array.from(
    { length: template.maxLevel - template.minLevel + 1 },
    (_, i) => (template.minLevel + i) as Level
  );

  const customerCustomizationByKey = new Map(
    customerCustomizations.propertyDescriptions.map(({ propertyKey, description }) => [propertyKey, description])
  );

  const onStartEdit = () => {
    updateMutation.reset();
    setDraft(new Map(savedScores));
    setDraftRationale(rationale ?? "");
    hover.suppressUntilNextPointerMove();
  };
  const onCancel = () => {
    returnFocusRef.current = true;
    setDraft(null);
    hover.setPreview(null);
    setDraftRationale("");
    updateMutation.reset();
  };
  const handleSave = () => {
    if (!draft || updateMutation.isPending) return;
    const scores = Array.from(draft, ([propertyKey, value]) => ({ propertyKey, value }));
    updateMutation.mutate(
      { scores, rationale: draftRationale },
      {
        onSuccess: () => {
          returnFocusRef.current = true;
          setDraft(null);
          hover.setPreview(null);
          setDraftRationale("");
        },
      }
    );
  };
  const onSetScore = (propertyKey: ClassificationPropertyKey, value: Level) => {
    const next = new Map(draft);
    next.set(propertyKey, value);
    setDraft(next);
  };
  const onClearScore = (propertyKey: ClassificationPropertyKey) => {
    const next = new Map(draft);
    next.delete(propertyKey);
    setDraft(next);
    hover.setPreview(null);
  };

  const scoreByKey = draft ?? savedScores;

  const cardSx: SxProps<Theme> = {
    mx: -2,
    p: 2,
    pb: isEditing ? 2 : 2.5,
    gap: 2,
    borderRadius: 1,
    border: "1px solid",
    borderColor: theme => (isEditing ? theme.palette.grey["300"] : "transparent"),
    backgroundColor: theme => (isEditing ? theme.palette.grey["50"] : theme.palette.background.paper),
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    ...(isInteractive && {
      "transition": "background-color 120ms ease",
      "&:hover": {
        borderColor: "transparent",
        backgroundColor: theme => theme.palette.action.hover,
      },
    }),
  };

  const onEditKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
    if (event.key === "Escape" && !updateMutation.isPending) {
      event.preventDefault();
      onCancel();
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSave();
    }
  };

  const cardContent = (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} sx={{ alignItems: "stretch" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 240,
            display: "grid",
            gridTemplateColumns: "25% 1fr",
            rowGap: 1.5,
            columnGap: 2,
            alignItems: "center",
          }}
        >
          <Stack direction="row" sx={{ gap: CELL_GAP, gridColumn: 2, mb: -0.5 }}>
            {levels.map(level => (
              <Typography
                key={level}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: theme => theme.typography.pxToRem(10),
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {level}
              </Typography>
            ))}
            <Box sx={{ ml: 0.5, width: BAR_HEIGHT, flexShrink: 0 }} />
          </Stack>
          {template.properties.map(property => (
            <PropertyRow
              key={property.key}
              property={property}
              customerCustomization={customerCustomizationByKey.get(property.key) ?? null}
              levels={levels}
              score={scoreByKey.get(property.key) ?? null}
              isEditing={isEditing}
              isSaving={updateMutation.isPending}
              hoverPreview={updateMutation.isPending ? null : hover.preview}
              onSetScore={onSetScore}
              onClearScore={onClearScore}
              onHoverPreview={hover.setPreview}
            />
          ))}
        </Box>
        <CriticalityDisplay criticality={criticality} isHidden={isEditing} />
      </Stack>
      <RationaleField
        isEditing={isEditing}
        value={isEditing ? draftRationale : rationale}
        onChange={setDraftRationale}
      />
      {isEditing && (
        <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={onCancel}>
            {t("Classification.Edit.Cancel")}
          </Button>
          <Button size="small" variant="contained" loading={updateMutation.isPending} onClick={handleSave}>
            {t("Classification.Edit.Save")}
          </Button>
        </Stack>
      )}
    </>
  );

  return (
    <>
      {isInteractive ? (
        <ButtonBase
          ref={node => {
            if (!node || !returnFocusRef.current) return;
            returnFocusRef.current = false;
            focusOnMount(node);
          }}
          onClick={onStartEdit}
          aria-label={t("Classification.Edit.TriggerAria")}
          sx={cardSx}
        >
          {cardContent}
        </ButtonBase>
      ) : (
        <Box
          ref={isEditing ? focusOnMount : undefined}
          tabIndex={isEditing ? -1 : undefined}
          sx={{ ...cardSx, outline: "none" }}
          onKeyDown={isEditing ? onEditKeyDown : undefined}
        >
          {cardContent}
        </Box>
      )}
      {updateMutation.isError && <DynamicForm.ErrorMessage errorMessage={t("Classification.Edit.SaveError")} />}
    </>
  );
};

const focusOnMount = (node: HTMLElement | null) => node?.focus({ preventScroll: true });

const BAR_HEIGHT = 32;
const BAR_TRANSITION = "all 150ms cubic-bezier(.4, 0, .2, 1)";
const RESET_BUTTON_CLASS = "property-reset-button";
const FILL_ACTIVE_CLASS = "cell-fill-active";
const CELL_GAP = 0.5;

type PropertyRowProps = {
  property: TemplateProperty;
  customerCustomization: string | null;
  levels: ReadonlyArray<Level>;
  score: Level | null;
  isEditing: boolean;
  isSaving: boolean;
  hoverPreview: HoverPreview | null;
  onSetScore: (propertyKey: ClassificationPropertyKey, value: Level) => void;
  onClearScore: (propertyKey: ClassificationPropertyKey) => void;
  onHoverPreview: (preview: HoverPreview | null) => void;
};

const PropertyRow: FC<PropertyRowProps> = ({
  property,
  customerCustomization,
  levels,
  score,
  isEditing,
  isSaving,
  hoverPreview,
  onSetScore,
  onClearScore,
  onHoverPreview,
}) => {
  const { t } = useTranslation();
  const max = levels[levels.length - 1];
  const hoveredLevel =
    isEditing && hoverPreview && hoverPreview.propertyKey === property.key ? hoverPreview.level : null;
  const displayScore = hoveredLevel ?? score;

  return (
    <>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.4 }}>
            {property.label}
          </Typography>
          {customerCustomization && (
            <CustomerCustomizationInfo customerCustomization={customerCustomization} label={property.label} />
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(10),
            lineHeight: 1.2,
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {property.description}
        </Typography>
      </Box>
      <Stack
        direction="row"
        role={isEditing ? undefined : "img"}
        aria-label={
          isEditing
            ? undefined
            : score === null
              ? t("Classification.Property.AriaNotScored", { property: property.label })
              : t("Classification.Property.Aria", { property: property.label, value: score, max })
        }
        onMouseLeave={isEditing ? () => onHoverPreview(null) : undefined}
        sx={[
          {
            gap: CELL_GAP,
            alignSelf: "start",
          },
          isEditing && {
            "&:hover": {
              [`& .${FILL_ACTIVE_CLASS}`]: { opacity: 0.9 },
              [`& .${RESET_BUTTON_CLASS}`]: { opacity: 1 },
            },
          },
        ]}
      >
        {levels.map(level => (
          <Cell
            key={level}
            propertyKey={property.key}
            level={level}
            filled={displayScore !== null && level <= displayScore}
            isEditing={isEditing}
            disabled={isSaving}
            selected={score === level}
            ariaLabel={t("Classification.Edit.SegmentAria", { property: property.label, level })}
            onSelect={() => onSetScore(property.key, level)}
            onHover={() => onHoverPreview({ propertyKey: property.key, level })}
          />
        ))}
        {isEditing ? (
          <IconButton
            className={RESET_BUTTON_CLASS}
            size="small"
            disabled={score === null || isSaving}
            onClick={() => onClearScore(property.key)}
            aria-label={t("Classification.Edit.ClearScoreAria", { property: property.label })}
            sx={{
              ml: 0.5,
              width: BAR_HEIGHT,
              height: BAR_HEIGHT,
              opacity: 0,
              transition: "opacity 150ms",
              visibility: score === null ? "hidden" : "visible",
            }}
          >
            <XIcon />
          </IconButton>
        ) : (
          <Box sx={{ ml: 0.5, width: BAR_HEIGHT, height: BAR_HEIGHT, flexShrink: 0 }} />
        )}
      </Stack>
    </>
  );
};

const CustomerCustomizationInfo: FC<{ customerCustomization: string; label: string }> = ({
  customerCustomization,
  label,
}) => {
  const { t } = useTranslation();
  const { anchorProps, popoverProps } = useHoverPopover();
  return (
    <>
      <Box
        component="span"
        {...anchorProps}
        onClick={event => event.stopPropagation()}
        aria-label={t("Classification.Property.InfoAria", { property: label })}
        sx={{
          "position": "relative",
          "display": "inline-flex",
          "&:before": { content: "''", position: "absolute", inset: 0, transform: "scaleX(3.5) scaleY(2.3)" },
        }}
      >
        <InfoIcon strokeWidth={2.5} size={10} color={theme => theme.palette.primary.main} />
      </Box>
      <HoverPopover {...popoverProps}>
        <Box sx={{ py: 1.5, px: 2, maxWidth: "20rem" }}>
          <Typography variant="body2" sx={{ fontSize: theme => theme.typography.pxToRem(12), textWrap: "pretty" }}>
            {customerCustomization}
          </Typography>
        </Box>
      </HoverPopover>
    </>
  );
};

type CellProps = {
  propertyKey: ClassificationPropertyKey;
  level: Level;
  filled: boolean;
  isEditing: boolean;
  disabled: boolean;
  selected: boolean;
  ariaLabel: string;
  onSelect: () => void;
  onHover: () => void;
};

const Cell: FC<CellProps> = ({
  propertyKey,
  level,
  filled,
  isEditing,
  disabled,
  selected,
  ariaLabel,
  onSelect,
  onHover,
}) => {
  const baseSx = (theme: Theme) => ({
    position: "relative" as const,
    flex: 1,
    height: BAR_HEIGHT,
    transition: BAR_TRANSITION,
    borderRadius: `${Number(theme.shape.borderRadius) / 2}px`,
    border: `1px solid ${theme.palette.grey["300"]}`,
    backgroundColor: lighten(theme.palette.grey["100"], 0.5),
  });

  const fill = (
    <Box
      className={filled ? FILL_ACTIVE_CLASS : undefined}
      sx={theme => {
        const color = CLASSIFICATION_COLORS(theme)[propertyKey][level];
        return {
          position: "absolute",
          inset: "-1px",
          borderRadius: "inherit",
          border: `1px solid ${color.border}`,
          backgroundColor: color.fill,
          mixBlendMode: "multiply",
          opacity: filled ? 1 : 0,
          transition: filled ? BAR_TRANSITION : "opacity 600ms cubic-bezier(.05, .85, .2, 1)",
          pointerEvents: "none",
        };
      }}
    />
  );

  if (!isEditing) {
    return <Box sx={baseSx}>{fill}</Box>;
  }

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onSelect}
      onMouseEnter={onHover}
      onFocus={onHover}
      aria-label={ariaLabel}
      aria-pressed={selected}
      sx={[
        baseSx,
        theme => ({
          "&:focus-visible": { outline: "none" },
          "&:focus-visible::after": {
            content: '""',
            position: "absolute",
            inset: "0px",
            borderRadius: "inherit",
            border: `2px solid ${CLASSIFICATION_COLORS(theme)[propertyKey][level].border}`,
            pointerEvents: "none",
          },
        }),
      ]}
    >
      {fill}
    </ButtonBase>
  );
};

const RationaleField: FC<{
  isEditing: boolean;
  value: string | undefined;
  onChange: (value: string) => void;
}> = ({ isEditing, value, onChange }) => {
  const { t } = useTranslation();

  if (!isEditing && !value) return null;

  return (
    <Box component="label" sx={{ display: "block", mt: 1.5 }}>
      <Typography
        component="div"
        sx={{
          fontSize: theme => theme.typography.pxToRem(10),
          fontWeight: 600,
          letterSpacing: "0.08em",
          color: theme => theme.palette.text.tertiary,
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        {t("Classification.Edit.RationaleLabel")}
      </Typography>
      {isEditing ? (
        <TextField
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          size="small"
          placeholder={t("Classification.Edit.RationalePlaceholder")}
          value={value ?? ""}
          onChange={event => onChange(event.target.value)}
          onClick={event => event.stopPropagation()}
        />
      ) : (
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            lineHeight: 1.5,
            color: theme => theme.palette.text.primary,
            whiteSpace: "pre-wrap",
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const CriticalityDisplay: FC<{
  criticality: Criticality | null | undefined;
  isHidden: boolean;
}> = ({ criticality, isHidden }) => {
  const { t } = useTranslation();
  return (
    <Stack
      component="section"
      aria-label={t("Classification.Criticality.Label")}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        mt: { xs: 2, sm: 0 },
        pr: 2.5,
        py: 1.5,
        minWidth: 120,
        visibility: isHidden ? "hidden" : "visible",
      }}
    >
      <Typography
        sx={{
          fontSize: theme => theme.typography.pxToRem(10),
          fontWeight: 600,
          letterSpacing: "0.08em",
          color: theme => theme.palette.text.tertiary,
          textTransform: "uppercase",
        }}
      >
        {t("Classification.Criticality.Label")}
      </Typography>
      {criticality ? (
        <>
          <Typography
            sx={{
              fontWeight: 700,
              mt: 1,
              fontSize: theme => theme.typography.pxToRem(36),
              lineHeight: 1.1,
              color: theme => theme.palette.text.primary,
            }}
          >
            {criticality.value}
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              mt: 0.25,
              fontSize: theme => theme.typography.pxToRem(12),
              letterSpacing: "0.01em",
              color: theme => theme.palette.text.tertiary,
            }}
          >
            {t(`Classification.Label.${criticality.labelKey}`)}
          </Typography>
        </>
      ) : (
        <Typography
          sx={{
            fontWeight: 500,
            mt: 0.5,
            fontSize: theme => theme.typography.pxToRem(20),
            lineHeight: 1.2,
            color: theme => theme.palette.text.tertiary,
          }}
        >
          –
        </Typography>
      )}
    </Stack>
  );
};
